import { DataTable, Stack, Text } from '@zorro/web/design-system';
import { MonolithMethods, useBatchCallEndpoint } from '@zorro/web/ui-utils';

import { ModalFooter } from '../../ModalComponents';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

const methodName = 'employeesControllerDelete' as const;
type BatchItemType = {
  key: string;
  params: Parameters<MonolithMethods[typeof methodName]>;
};

export type DeleteEmployeeFormProps = {
  recordsForActions: RecordForActions[];
  onClose: () => void;
  onSuccess: () => void;
};

export const DeleteEmployeeForm = ({
  recordsForActions,
  onClose,
  onSuccess,
}: DeleteEmployeeFormProps) => {
  const { executeBatchCall, errors } = useBatchCallEndpoint({
    singularItemName: 'employee',
    action: 'deleted',
    batchSize: 5,
    methodName,
  });

  const deleteEmployees = async () => {
    const batchItems: BatchItemType[] = recordsForActions.map(
      ({ employeeId, fullName }) => ({
        key: fullName,
        params: [employeeId],
      })
    );

    await executeBatchCall(batchItems, { onSuccess });
  };

  async function handleSubmit() {
    await deleteEmployees();
  }

  return (
    <Stack>
      {errors.length > 0 ? (
        <ErrorsTable
          tableTitle="Employee name"
          errors={errors}
          isBulk={recordsForActions.length > 1}
          shouldHideOnSingle={false}
        />
      ) : (
        <Stack>
          <Text>
            This will permanently delete the following employees. Are you sure
            you want to continue?
          </Text>
          <DataTable
            columns={[
              { accessor: 'fullName' },
              { accessor: 'email' },
              { accessor: 'employeeId' },
            ]}
            records={recordsForActions}
          />
          <ModalFooter
            onSubmit={handleSubmit}
            onClose={onClose}
            submitText="Delete employees"
            submitVariant="negative"
          />
        </Stack>
      )}
    </Stack>
  );
};
